'use client';

import React, { useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useFormState } from 'react-use-form-state';
import { z } from 'zod';

import Button from 'molecules/Button';
import ContactSidebar from 'molecules/ContactSideBar';
import RadioGroup from 'molecules/Form/radiogroup/radiogroup';
import SelectInput from 'molecules/Form/selectinput/selectinput';
import Textarea from 'molecules/Form/textarea/textarea';
import TextInput from 'molecules/Form/textinput/textinput';

import { usStates } from 'components/Forms/AgentQuote/data/states';
import styles from 'components/Forms/ContactForm/contactform.module.scss';

import { formZodSchema } from 'utils/formZodSchema';
import { debounce, formatPhoneNumber, isNonEmptyArray, isValidNpi } from 'utils/functions';
import { ENV_KEY_MAPS, getEnvValue } from 'utils/getEnvValue';
import SendGtmEvent from 'utils/hooks/sendGtmEvent';

import type { ContactFormProps } from 'components/Forms/ContactForm/config';
import type { ChangeEvent, FC, FormEvent } from 'react';
import type { ReCAPTCHAProps } from 'react-google-recaptcha';

interface ContactAgentFormProps extends ContactFormProps {
  formId?: string;
}

const ReCAPTCHAComponent = ReCAPTCHA as unknown as React.ForwardRefExoticComponent<
  React.PropsWithoutRef<ReCAPTCHAProps> & React.RefAttributes<ReCAPTCHA>
>;

const ContactForm: FC<ContactAgentFormProps> = ({
  formId = '',
  title,
  formFields,
  redirect_url,
  addPadding,
  contactSideBar,
  outline = 'false',
}) => {
  const reCaptcha = true;
  // read only form state management
  const [formState, form] = useFormState();
  // destructure formState and form
  const { validity, values } = formState;
  const { text, email, tel, textarea, select } = form;
  const [formError, setFormError] = useState<string>('');
  const [inputErrorMessages, setInputErrorMessages] = useState<Record<string, string>>({});
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);

  const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || '';

  const formUrl = getEnvValue(redirect_url, ENV_KEY_MAPS.urls);

  const fieldNotEmpty = (field = '') => field.trim().length > 0;
  const allFieldsValid = (valid: any) => {
    const validatedFields = Object.keys(valid);

    return validatedFields.every(key => valid[key]);
  };

  // Build required field forms
  const requiredFields = isNonEmptyArray(formFields)
    ? formFields
        .filter(field => field.required)
        .map(field => field.fieldName)
        .filter((fieldName): fieldName is string => Boolean(fieldName))
    : [];

  // Schema Key for zodSchema
  const nameToSchemaKey: Record<string, keyof typeof formState.values> = {
    'First name': 'firstName',
    'Last name': 'lastName',
    'Email address': 'email',
    County: 'county',
    phone: 'phone',
    // Some fields come in with dashes instead of spaces or camelCase
    'first-name': 'firstName',
    'last-name': 'lastName',
    'email-address': 'email',
  };

  // Tracks touched fields
  const touched = useRef<Record<string, boolean>>({});

  // Stores debounced validators per fields
  const changeDebounceMap = useRef<Record<string, ReturnType<typeof debounce>>>({});
  const blurDebounceMap = useRef<Record<string, ReturnType<typeof debounce>>>({});

  // ValidatesField Input, used by onChange and onBlur
  const validateFieldInput = (fieldName: string, value: string, required?: boolean) => {
    const requiredField = required;
    const validationValue = value;
    const formFieldName = nameToSchemaKey[fieldName];

    setInputErrorMessages(prev => {
      const nextErrorMessages = { ...prev };

      // Validation for radio and select inputs
      if (fieldName === 'Select Profession' || fieldName === 'State') {
        if (!validationValue) {
          formState.setFieldError(fieldName, false);
          nextErrorMessages[fieldName] = 'This field is required.';
        } else {
          formState.setField(fieldName, validationValue);
          formState.validity[fieldName] = true;
          nextErrorMessages[fieldName] = '';
        }
      } else if (fieldName === 'NPI Number') {
        const cleaned = validationValue.replace(/[^0-9]/g, '');
        const npiChunks: string[] = cleaned.match(/\d{1,10}/g) || [];
        const allValid = npiChunks.every(isValidNpi);

        if (npiChunks.length === 0 || allValid) {
          formState.setField(fieldName, validationValue);
          formState.validity[fieldName] = true;
          nextErrorMessages[fieldName] = '';
        } else {
          formState.setFieldError(fieldName, false);
          formState.validity[fieldName] = false;
          nextErrorMessages[fieldName] = 'The NPI number is invalid';
        }
      } else if (requiredField === true && validationValue?.trim().length === 0) {
        formState.setFieldError(fieldName, false);
        nextErrorMessages[fieldName] = 'This field is required.';
      } else {
        try {
          const partialSchema = formZodSchema.pick({ [formFieldName]: true } as any);
          partialSchema.parse({ [formFieldName]: validationValue });
          nextErrorMessages[fieldName] = '';
          formState.validity[fieldName] = true;
        } catch (error) {
          if (error instanceof z.ZodError) {
            const message = error.flatten().fieldErrors[formFieldName]?.[0] || 'Invalid input';
            nextErrorMessages[fieldName] = message;
            formState.setFieldError(fieldName, false);
          }
        }
      }

      const hasError = Object.values(nextErrorMessages).some(error => error.length > 0);

      // Check for unfilled required fields
      const { values: currentValues } = formState;
      const requiredFieldsEntered = requiredFields.every(field => {
        const val = currentValues[field];

        return typeof val === 'string' && val.trim().length > 0;
      });

      const hasSpecialFeilds = Boolean(currentValues['State'] || currentValues['Select Profession']);
      const specialFieldsFilled = currentValues['State']?.trim() && currentValues['Select Profession']?.trim();

      if (hasError) {
        setFormError('Please correct the highlighted fields.');
        setFormStatus('error');
      } else if (!requiredFieldsEntered || (!specialFieldsFilled && hasSpecialFeilds)) {
        setFormError('Please complete all required fields.');
        setFormStatus('error');
      } else {
        setFormError('');
        setFormStatus('success');
      }

      return nextErrorMessages;
    });
  };

  // validates on change with debounce to give user time to type
  const validateOnChange = (fieldName: string, value: string, required?: boolean) => {
    // Check for empty input
    touched.current[fieldName] = true;
    if (!changeDebounceMap.current[fieldName]) {
      changeDebounceMap.current[fieldName] = debounce((latestValue: string) => {
        // validation step
        if (touched.current[fieldName]) {
          validateFieldInput(fieldName, latestValue, required);
        }
      }, 200);
    }

    changeDebounceMap.current[fieldName](value);
  };

  // validates on blur with debounce to give user time to type
  const validateOnBlur = (fieldName: string, value: string, required?: boolean) => {
    if (!blurDebounceMap.current[fieldName]) {
      touched.current[fieldName] = true;

      blurDebounceMap.current[fieldName] = debounce((latestValue: string) => {
        if (fieldName === 'phone') {
          const digits = latestValue.replace(/\D/g, '');
          const formatted = formatPhoneNumber(digits);

          formState.setField(fieldName, formatted); // Display formatted
          validateFieldInput(fieldName, digits); // Validate only raw digits

          return;
        }

        // Normal case for non-phone inputs
        if (latestValue && (!latestValue || latestValue.trim() === '')) {
          formState.setFieldError(fieldName, false);
        }

        validateFieldInput(fieldName, latestValue, required);
      }, 300);
    }

    blurDebounceMap.current[fieldName](value);
  };

  const validateFields = (fieldName: string, value: string, required: boolean) => {
    // Check for empty input
    if (required && (!value || value.trim() === '')) {
      formState.setFieldError(fieldName, false);
    }

    if (!required || reCaptcha) {
      return;
    }
  };

  // Handle Form Submit
  const handleSubmit = (e: FormEvent) => {
    const honeypotValue = (e.target as HTMLFormElement).username?.value;
    if (honeypotValue && honeypotValue.trim() !== '') {
      e.preventDefault();
      setFormStatus('error');

      return;
    }

    if (reCaptcha && !captchaToken) {
      e.preventDefault();
      setFormError('Please complete the reCAPTCHA.');
      setFormStatus('error');

      return;
    }

    const missingFields = requiredFields.filter(field => !fieldNotEmpty(values[field]));
    requiredFields.forEach(field => {
      const value = values[field];
      validateFieldInput(field, value, missingFields.includes(field));
    });

    // Check if there is a require field that is empty throw error
    if (missingFields.length > 0) {
      e.preventDefault();
      setFormError('Please fill in all required fields');
      setFormStatus('error');

      return false;
    }

    // if there is a field that is invalid throw error
    if (!allFieldsValid(validity)) {
      e.preventDefault();
      setFormError('There is an error in the form.');
      setFormStatus('error');

      return;
    }
    setFormStatus('success');
  };

  const handleCaptchaChange = async (token: string | null) => {
    try {
      if (!token) {
        setFormError('Please complete the reCAPTCHA.');

        return;
      }

      const res = await fetch('/api/recaptcha', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      });

      const data = await res.json();

      if (res.ok && data.success) {
        setCaptchaToken(token);
      } else {
        setFormError('reCAPTCHA verification failed.');
      }
    } catch (error) {
      setFormError('Error verifying reCAPTCHA. Please try again.');
    }
  };

  const handleCheckBoxChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    formState.setField(name, checked);
  };

  const getFormStateProps = (type: 'text' | 'email' | 'tel', fieldName: string) => {
    const handlers: Record<'text' | 'email' | 'tel', typeof text> = {
      text,
      email,
      tel,
    };

    return {
      ...handlers[type]({
        name: fieldName,
        validateOnBlur: true,
      }),
    };
  };

  return (
    <div className={`${styles.containerWrapper} ${addPadding ? styles.addPadding : styles.removePadding}`}>
      {formStatus !== 'idle' && (
        <SendGtmEvent
          variant="contact-one-model-form"
          eventData={{
            formSpecialty: values['Primary Specialty'],
            formState: values['State'],
            formZip: values['Zip'],
            formSubmitSuccessful: formStatus === 'success' ? true : false,
          }}
        />
      )}
      <div className={styles.containerText}>
        <div>
          <form
            id={formId}
            className={styles.form}
            name={formId}
            onSubmit={handleSubmit}
            action={formUrl}
            method="POST"
            noValidate
          >
            <input type="hidden" name="form-name" value={title} />
            <input
              type="text"
              name="username"
              id="username"
              className={styles.username}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
            />
            <fieldset className={outline === true ? styles.border : styles.borderNone}>
              <legend className={styles.legend}>{title}</legend>
              {formError && <p className={styles.errorText}>{formError}</p>}
              <div className={styles.grid}>
                {isNonEmptyArray(formFields) &&
                  formFields.map(({ type = 'text', fieldName = '', label = '', required, options }, index) => (
                    <div
                      key={index}
                      className={`${styles.addMargin} ${type === 'radio' ? styles.radioGroup : ''} ${
                        fieldName === 'NPI Number' ? styles.gridRow2 : ''
                      }`}
                    >
                      {(type === 'text' || type === 'tel' || type === 'email') && (
                        <TextInput
                          id={fieldName}
                          label={label}
                          maxLength={80}
                          required={required}
                          formStateProps={getFormStateProps(type, fieldName)}
                          valid={validity[fieldName]}
                          message={inputErrorMessages[fieldName]}
                          validate={validateFields}
                          validateOnBlur={validateOnBlur}
                          validateOnChange={validateOnChange}
                          className={styles.marginReset}
                        />
                      )}
                      {type === 'state' && (
                        <SelectInput
                          id={fieldName}
                          label={label}
                          options={usStates}
                          required
                          formStateProps={{
                            ...select({
                              name: fieldName,
                              validateOnBlur: true,
                            }),
                          }}
                          className={`${styles.marginReset} ${styles.labelAbsolute}`}
                          validateOnBlur={validateOnBlur}
                          validateOnChange={validateOnChange}
                          validate={validateFields}
                          valid={validity[fieldName]}
                        />
                      )}
                      {type === 'textarea' && (
                        <Textarea
                          id={fieldName}
                          label={label}
                          required={fieldName !== 'NPI Number' && required}
                          className={`${styles.marginReset} ${fieldName === 'NPI Number' ? `${styles.textArea}` : ''}`}
                          message={inputErrorMessages[fieldName]}
                          noAutoSize
                          validateOnBlur={validateOnBlur}
                          validateOnChange={validateOnChange}
                          validate={validateFields}
                          formStateProps={{
                            ...textarea({
                              name: fieldName,
                              validateOnBlur: true,
                            }),
                          }}
                          valid={validity[fieldName]}
                        />
                      )}
                      {type === 'checkbox' && (
                        <div key={index} className={styles.checkbox}>
                          <label htmlFor={label} className={styles.label}>
                            <input
                              type="checkbox"
                              name={label}
                              onChange={handleCheckBoxChange}
                              id={label}
                              className={styles.checkItem}
                              checked={Boolean(values[label || ''])}
                            />
                            {label}
                          </label>
                        </div>
                      )}
                      {type === 'radio' && isNonEmptyArray(options) && (
                        <RadioGroup
                          legend={label}
                          valid={validity[fieldName]}
                          required={required}
                          validate={validateFields}
                          validateOnBlur={validateOnBlur}
                          validateOnChange={validateOnChange}
                          options={options.map(option => ({
                            label: option.label || '',
                            formStateProps: {
                              name: fieldName,
                              value: option.label,
                              checked: values[fieldName] === option.label,
                              onChange: (e: any) => {
                                formState.setField(fieldName, e.target.value);
                                validateOnChange(fieldName, e.target.value);
                              },
                            },
                          }))}
                        />
                      )}
                    </div>
                  ))}
              </div>
              {reCaptcha && (
                <ReCAPTCHAComponent ref={recaptchaRef} sitekey={siteKey as string} onChange={handleCaptchaChange} />
              )}
              <Button id="contact-agent-form-submit-button" color="teal" type="submit" name="submit">
                Submit
              </Button>
            </fieldset>
          </form>
        </div>
      </div>
      <div className={styles.sideNav}>
        <ContactSidebar {...contactSideBar} />
      </div>
    </div>
  );
};

export default ContactForm;
